import {useAccessToken} from "../../services/auth"
import React, {useEffect, useState} from "react"
import useAsyncResultIdle from "../../hooks/useAsyncResultIdle"
import {useToasts} from "react-toast-notifications"
import {optionalHorizontalField} from "../../components/HorizontalField/HorizontalField"
import {BankSettings, SignatureAlgorithm, update} from "../../resources/bankSettings"
import Card from "../../containers/Card/Card"

export default function SamlSettings({bankSettings, refresh}: {bankSettings: BankSettings; refresh: () => void}) {
	const accessToken = useAccessToken()
	const [state, patch] = useAsyncResultIdle(update)
	const [bankWorkspace, setBankWorkspace] = useState(
		bankSettings.attributes.samlSettings ? bankSettings.attributes.samlSettings?.bankWorkspace : ""
	)
	const [entryPoint, setEntryPoint] = useState(
		bankSettings.attributes.samlSettings ? bankSettings.attributes.samlSettings?.entryPoint : ""
	)
	const [signatureAlgorithm, setSignatureAlgorithm] = useState<SignatureAlgorithm>(
		bankSettings.attributes.samlSettings ? bankSettings.attributes.samlSettings.signatureAlgorithm : "sha1"
	)
	const [cert, setCert] = useState(
		bankSettings.attributes.samlSettings ? bankSettings.attributes.samlSettings?.cert : ""
	)
	const [enforceSSOLogin, setEnforceSSOLogin] = useState(bankSettings.attributes.samlSettings?.enforceSSOLogin)
	const [enableTwoFactorAuthentication, setEnableTwoFactorAuthentication] = useState(
		bankSettings.attributes.enableTwoFactorAuthentication ?? false
	)
	const {addToast} = useToasts()

	useEffect(() => {
		setEnableTwoFactorAuthentication(bankSettings.attributes.enableTwoFactorAuthentication ?? false)
	}, [bankSettings])

	useEffect(() => {
		if (state.isOk()) {
			addToast("Bank Settings Updated Successfully", {appearance: "success"})
			refresh()
		}
	}, [state])

	return (
		<>
			<Card title="SAML">
				<form
					onSubmit={(e) => {
						e.preventDefault()
						if (bankWorkspace && entryPoint && cert && signatureAlgorithm) {
							patch(
								accessToken,
								{
									enableTwoFactorAuthentication,
									samlSettings: {
										bankWorkspace: bankWorkspace,
										entryPoint: entryPoint,
										cert: cert,
										signatureAlgorithm: signatureAlgorithm,
										enforceSSOLogin: enforceSSOLogin,
									},
								},
								bankSettings.id
							)
						}
					}}
				>
					<fieldset>
						<div className="field">
							<label className="label">Workspace Unique Name / Identifier</label>
							<div className="control">
								<input
									className="input"
									type="text"
									value={bankWorkspace}
									required
									onChange={(e) => setBankWorkspace(e.target.value)}
								/>
							</div>
						</div>
						<div className="field">
							<label className="label">Identity Provider Entry-point</label>
							<div className="control">
								<input
									className="input"
									type="url"
									value={entryPoint}
									required
									onChange={(e) => setEntryPoint(e.target.value)}
								/>
							</div>
						</div>
						<div className="field">
							<label className="label">Signature Algorithm</label>
							<div className="control">
								<select
									value={signatureAlgorithm}
									onChange={(e) => setSignatureAlgorithm(e.target.value as SignatureAlgorithm)}
								>
									<option key="sha1" value="sha1">
										sha1
									</option>
									<option key="sha256" value="sha256">
										sha256
									</option>
									<option key="sha512" value="sha512">
										sha512
									</option>
								</select>
							</div>
						</div>
						<div className="field">
							<label className="label">Identity Provider Certificate</label>
							<div className="control">
								<textarea
									placeholder="Certificate"
									className="textarea has-fixed-size"
									value={cert}
									required
									onChange={(e) => setCert(e.target.value)}
								/>
							</div>
						</div>
						<div className="field">
							<label className="label">
								<input
									type="checkbox"
									checked={enforceSSOLogin}
									onChange={(e) => setEnforceSSOLogin(e.target.checked)}
								/>
								&nbsp;Enforce SSO Login
							</label>
						</div>
						<div className="field">
							<label className="label">
								<input
									type="checkbox"
									checked={enableTwoFactorAuthentication}
									onChange={(e) => setEnableTwoFactorAuthentication(e.target.checked)}
								/>
								&nbsp;Two-Factor Authentication
							</label>
						</div>
					</fieldset>
					<div className="columns">
						<div className="column mt-4">
							{state.match(
								() => (
									<button className="button is-success">Update</button>
								),
								() => (
									<button className="button is-success is-loading">Updating</button>
								),
								(_) => null,
								(err) => (
									<>
										<div className="has-text-danger">{err.errors[0].title}</div>
										<button className="button is-success">Update</button>
									</>
								)
							)}
						</div>
					</div>
				</form>
			</Card>
			<Card>
				<label className="label">Unit (Service Provider) Settings:</label>
				{optionalHorizontalField(
					"Assertion URL",
					`${process.env.API_URL}/sso/saml/login/callback/${bankWorkspace ? bankWorkspace : "<workspace>"}`
				)}
				{optionalHorizontalField(
					"Issuer",
					`${process.env.API_URL}/sso/saml/metadata/${bankWorkspace ? bankWorkspace : "<workspace>"}`
				)}
			</Card>
		</>
	)
}
